/**
 * A11-003 (keyboard-only donor journey) — where focus goes when checkout closes
 * over the widget overlay.
 *
 * Rule: when the hosted script forwards EXIT_CHECKOUT_FORM, focus returns to the
 * control that opened checkout if it is still present; otherwise to the widget's
 * primary action; otherwise to the overlay's mobile opener. "Present" means
 * connected, in the active step, rendered, and on screen or reachable by
 * scrolling. A present target that is still disabled is waited for, not
 * skipped.
 *
 * Why: the hosted script puts top-page focus back on the overlay frame, but
 * inside that document focus had nowhere to go. The opener sat on a step the
 * form had since left, or was still disabled because the post-DONATE reset had
 * not run, so the donor landed on <body> (or, in the mobile drawer, on the
 * overlay's own Support button, translated off the bottom of the screen).
 */
import { isRenderedForTab } from '@charitystack/shared';

/** Fired by scheduleFormReset on every DONATE (formResetUtils.js). */
export const CHECKOUT_TRIGGERED_EVENT = 'charitystack:checkout-triggered';

/** Marks the overlay's own "Support" button that opens the mobile drawer. */
export const OVERLAY_OPENER_ATTRIBUTE = 'data-cs-overlay-opener';

function isScrollable(element) {
  const style = element.ownerDocument.defaultView.getComputedStyle(element);
  return (
    /(auto|scroll)/.test(`${style.overflowY} ${style.overflowX}`) &&
    (element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth)
  );
}

/**
 * On screen, or inside a scroll container that can bring it on screen. An
 * element pushed off the viewport by a transform (the mobile opener while the
 * drawer is open) is neither.
 */
export function isOnScreenOrScrollable(element) {
  const win = element.ownerDocument.defaultView;
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return false;
  const onScreen =
    rect.bottom > 0 && rect.right > 0 && rect.top < win.innerHeight && rect.left < win.innerWidth;
  if (onScreen) return true;
  for (let node = element.parentElement; node && node !== element.ownerDocument.body; node = node.parentElement) {
    if (isScrollable(node)) return true;
  }
  return false;
}

/**
 * A step that just became active can hand its controls a `visibility`
 * transition (they carry `transition-all`), and they read as not rendered until
 * it ends. Such a control is about to be present, not absent.
 */
function isBecomingVisible(element) {
  return (element.getAnimations?.() || []).some(
    (animation) => animation.transitionProperty === 'visibility' && animation.playState === 'running',
  );
}

/** Rendered now, or about to be (see isBecomingVisible). */
function isRenderedOrBecoming(element) {
  return isRenderedForTab(element) || isBecomingVisible(element);
}

/**
 * Present as a focus target, ignoring `disabled` and a visibility transition
 * still running: connected, not inside an
 * inert, hidden or aria-hidden subtree, in the active step, rendered, and on
 * screen or scrollable to.
 *
 * Its own step (nearest `.page`) must be the active one. Visibility is read off
 * the element, not off an ancestor step class: the drawer copy of the form sits
 * inside an inactive outer page that its active step makes visible again, and a
 * button with a transition still reads visible for the length of a step switch.
 */
function isPresentFocusTarget(element) {
  if (!element?.isConnected || typeof element.focus !== 'function') return false;
  if (element === element.ownerDocument.body) return false;
  if (element.closest('[inert], [hidden], [aria-hidden="true"]')) return false;
  const ownStep = element.closest('.page');
  if (ownStep && !ownStep.classList.contains('active')) return false;
  return isRenderedOrBecoming(element) && isOnScreenOrScrollable(element);
}

/** Present, enabled and rendered now. */
export function isVisibleFocusTarget(element) {
  return isPresentFocusTarget(element) && !element.disabled && isRenderedForTab(element);
}

function candidatesFor(opener, doc) {
  return [
    opener,
    ...doc.querySelectorAll('.form-action-buttons button'),
    ...doc.querySelectorAll(`[${OVERLAY_OPENER_ATTRIBUTE}]`),
  ];
}

/** The primary action of the visible form step, or the overlay's mobile opener. */
export function findPrimaryAction(root) {
  return candidatesFor(null, root).find(isVisibleFocusTarget) || null;
}

/**
 * The first present candidate, disabled or not, settled or not. A disabled or
 * still-appearing one is returned so the caller waits for it rather than
 * dropping to a worse target (the Back button beside a Next still fading in).
 */
export function pickFocusTarget(opener, doc) {
  return candidatesFor(opener, doc).find(isPresentFocusTarget) || null;
}

/**
 * True when the donor already has focus on a real control: visible and in the
 * Tab order. A drawer container (tabindex -1) that a focus trap parked focus on
 * does not count, and neither does <body>.
 */
export function hasControlFocus(doc) {
  const active = doc.activeElement;
  return isVisibleFocusTarget(active) && active.tabIndex >= 0;
}

/**
 * Move focus back after checkout closes. Returns the element focused, or null
 * when the best target is still disabled or nothing is present.
 *
 * @param {Element | null} opener the element that had focus when checkout opened
 * @param {Document} doc the overlay's document
 */
export function returnFocusAfterCheckout(opener, doc) {
  // Never act while another frame holds focus: the donor may already be back
  // in a reopened checkout, and focus() here would pull them out of it.
  if (!doc.hasFocus()) return null;
  const target = pickFocusTarget(opener, doc);
  if (!target || target.disabled || !isRenderedForTab(target)) return null;
  const rect = target.getBoundingClientRect();
  const win = doc.defaultView;
  const onScreen = rect.bottom > 0 && rect.right > 0 && rect.top < win.innerHeight && rect.left < win.innerWidth;
  target.focus({ preventScroll: onScreen });
  return doc.activeElement === target ? target : null;
}

/**
 * Return focus now, and try again whenever the situation changes, until
 * `timeoutMs` passes. Returns a cancel function.
 *
 * It tries again on:
 *  - the frame's own `focus`: the host can hold the overlay frame `inert`
 *    around the exit, and focus() into an inert frame is silently refused, and
 *    the frame can gain focus while still inert. Keying on the frame's own
 *    event means it never pulls focus out of the host page.
 *  - a `focusin` that lands on something that is not a real control: the
 *    widget finishes its post-DONATE reset on the same exit message, so the
 *    opener's step can hide just after the first attempt and the step-focus
 *    hook parks focus on the new step's container.
 *  - a `transitionend`: the chosen target may still be fading in with its
 *    newly active step.
 *  - a DOM or `disabled` change: the chosen target may still be disabled
 *    because the reset has not run yet. It is waited for, not skipped; if it
 *    never enables inside `timeoutMs`, the best enabled target is used.
 *
 * It never moves focus while this document lacks focus, and it stops for good
 * on the next DONATE (`charitystack:checkout-triggered`): a donor who reopens
 * checkout inside the window must not be pulled back out of it.
 *
 * @param {Element | null} opener
 * @param {Document} doc
 * @param {{ timeoutMs?: number }} [options]
 */
export function scheduleFocusReturnAfterCheckout(opener, doc, { timeoutMs = 3000 } = {}) {
  const win = doc.defaultView;
  const attempt = () => {
    // Leave a donor who already has a real control focused alone.
    if (!hasControlFocus(doc)) returnFocusAfterCheckout(opener, doc);
  };
  const observer =
    typeof win.MutationObserver === 'function' ? new win.MutationObserver(() => attempt()) : null;
  // If the waited-for target never enables, settle for the best enabled one.
  const timer = win.setTimeout(() => {
    cancel();
    if (!doc.hasFocus() || hasControlFocus(doc)) return;
    const fallback = candidatesFor(opener, doc).find(isVisibleFocusTarget);
    fallback?.focus({ preventScroll: true });
  }, timeoutMs);
  function cancel() {
    win.removeEventListener(CHECKOUT_TRIGGERED_EVENT, cancel);
    win.removeEventListener('focus', attempt);
    doc.removeEventListener('focusin', attempt);
    doc.removeEventListener('transitionend', attempt, true);
    observer?.disconnect();
    win.clearTimeout(timer);
  }
  attempt();
  // A reopened checkout (any DONATE) ends this return: the donor is heading
  // back into checkout, and nothing here may follow them.
  win.addEventListener(CHECKOUT_TRIGGERED_EVENT, cancel);
  win.addEventListener('focus', attempt);
  doc.addEventListener('focusin', attempt);
  // A target still fading in is retried when its transition ends.
  doc.addEventListener('transitionend', attempt, true);
  observer?.observe(doc.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['disabled', 'class', 'style', 'inert', 'hidden', 'aria-hidden'],
  });
  return cancel;
}
